import React, { useState } from 'react';

/** Splits on a separator, but not inside parentheses: "a (b, c), d" by "," is "a (b, c)" and "d". */
export const splitTop = (text: string, separator: string): string[] => {
    const parts: string[] = [];
    let depth = 0;
    let current = '';
    for (const ch of text) {
        if (ch === '(') depth++;
        if (ch === ')') depth = Math.max(0, depth - 1);
        if (ch === separator && depth === 0) {
            parts.push(current);
            current = '';
        } else current += ch;
    }
    parts.push(current);
    return parts.map((p) => p.trim()).filter(Boolean);
};

export interface Fact {
    label?: string;
    sub?: string;
    items: string[];
    text?: string;
}

const LABELLED = /^([^:()]{1,40}):\s*([\s\S]+)$/;

/**
 * A measured line of the checklist as rows: "prod: 4 zonder rij: A, B; prodwbtr: …" becomes a row per environment
 * with the four names as chips, and "A aan in prodwbtr; B aan in prod" a row per environment with what is on there.
 */
export const parseFacts = (text: string): Fact[] => {
    const parts = splitTop(text, ';');
    const toggles = parts.map((p) => /^(.+?) (aan|uit) in (\S+)$/.exec(p));

    if (parts.length > 1 && toggles.every(Boolean)) {
        const groups = new Map<string, string[]>();
        toggles.forEach((m) => {
            const key = `${m![2]} in ${m![3]}`;
            groups.set(key, [...(groups.get(key) ?? []), m![1]]);
        });
        return Array.from(groups, ([label, items]) => ({ label, items }));
    }

    return parts.map((part) => {
        const top = LABELLED.exec(part);
        if (!top) return { items: [], text: part };
        const sub = LABELLED.exec(top[2]);
        const rest = sub ? sub[2] : top[2];
        const items = splitTop(rest, ',');
        return items.length > 1 ? { label: top[1], sub: sub?.[1], items } : { label: top[1], sub: sub?.[1], items: [], text: rest };
    });
};

const Chips = ({ items, max = 6 }: { items: string[]; max?: number }) => {
    const [all, setAll] = useState(false);
    const shown = all ? items : items.slice(0, max);
    return (
        <span className="hrow" style={{ gap: 5 }}>
            {shown.map((item, i) => (
                <span key={i} className="fact-chip">{item}</span>
            ))}
            {items.length > max && (
                <button className="fact-chip more" onClick={() => setAll(!all)}>
                    {all ? 'minder' : `+${items.length - max}`}
                </button>
            )}
        </span>
    );
};

/** What a check measured, as short rows instead of one long line. */
export const Facts = ({ text }: { text: string }) => (
    <div className="facts">
        {parseFacts(text).map((f, i) => (
            <div key={i} className="fact">
                {f.label && <span className="fact-label mono">{f.label}</span>}
                <span className="fact-body">
                    {f.sub && <span className="mut">{f.sub}</span>}
                    {f.items.length > 0 ? <Chips items={f.items} /> : <span>{f.text}</span>}
                </span>
            </div>
        ))}
    </div>
);
